import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Mail, Lock, User, Github, Chrome, 
  ShieldCheck, Sparkles 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Google OAuth Kütüphanesi
import { useGoogleLogin } from '@react-oauth/google';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import FoxLogo from '../components/FoxLogo';
import { useToast } from '../hooks/use-toast';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { login, register, googleLogin } = useAuth(); 
  const { toast } = useToast();
  const { t } = useLanguage(); 
   
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  // --- 1. E-POSTA İLE GİRİŞ/KAYIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
        let result;
        if (isLogin) {
            result = await login(formData.email, formData.password);
        } else {
            result = await register(formData.name, formData.email, formData.password);
        }

        if (result.success) {
            toast({
                title: isLogin ? (t('toast_welcome_back') || "Welcome Back") : (t('toast_register_success') || "Registration Successful"),
                description: t('toast_login_success') || "Successfully logged in, redirecting...",
                className: "bg-emerald-600 text-white border-0"
            });
            navigate('/dashboard');
        } else {
            toast({
                title: t('toast_failed') || "Operation Failed",
                description: result.error || "Login failed.",
                variant: "destructive"
            });
        }
    } catch (error) {
        toast({ title: "Error", description: "Unexpected error occurred.", variant: "destructive" });
    } finally {
        setLoading(false);
    }
  };

  // --- 2. GOOGLE İLE GİRİŞ (POPUP AÇAN FONKSİYON) ---
  const performGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
        setLoading(true);
        try {
            console.log("Google Token Received:", tokenResponse.access_token);
            
            // 1. Token'ı alıp Backend'e gönderiyoruz (api.js -> googleLogin)
            const result = await googleLogin(tokenResponse.access_token);
            
            if (result.success) {
                toast({
                    title: t('toast_google_success') || "Google Login Successful",
                    description: "Account verified.",
                    className: "bg-blue-600 text-white border-0"
                });
                navigate('/dashboard');
            } else {
                toast({
                    title: t('toast_google_failed') || "Login Failed",
                    description: result.error || "Server could not verify Google identity.",
                    variant: "destructive"
                });
            }
        } catch (error) {
            console.error("Google Login Flow Error:", error);
            toast({ title: "Connection Error", description: "Could not reach server.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    },
    onError: (error) => {
        console.error("Google Login Failed:", error);
        toast({ title: "Google Error", description: "Google window closed or error occurred.", variant: "destructive" });
        setLoading(false);
    }
  });

  return (
    // Global Arka Plan (#0B1120)
    <div className="min-h-screen bg-[#0B1120] text-white font-sans flex relative overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-orange-600/10 rounded-full blur-[150px]"></div>
      </div>

      {/* LEFT SIDE: BRANDING (Hidden on Mobile) */}
      <div className="hidden lg:flex w-1/2 relative z-10 flex-col justify-between p-12 bg-white/[0.02] border-r border-white/5">
          <div>
              <div className="flex items-center gap-3 mb-8">
                  <div className="bg-gradient-to-br from-orange-600 to-orange-500 p-2.5 rounded-xl shadow-lg shadow-orange-600/20">
                      <FoxLogo size={24} className="text-white" />
                  </div>
                  <span className="text-2xl font-black tracking-tighter uppercase italic text-white leading-none">
                      Büro<span className="text-orange-500">Fuchs</span>
                  </span>
              </div>
              
              <div className="space-y-6 max-w-lg mt-20">
                  <h1 className="text-6xl font-black tracking-tighter leading-[0.9]">
                      {t('welcome_title') || 'Welcome to'} <br/>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">{t('future_work') || 'The Future of Work.'}</span>
                  </h1>
                  <p className="text-lg text-slate-400 font-medium leading-relaxed">
                      {t('auth_desc') || "Manage all your bureaucratic and financial processes in Germany from a single intelligent hub."}
                  </p>
              </div>
          </div>

          <div className="space-y-6">
              <div className="flex gap-4">
                  <div className="glass-panel p-4 rounded-2xl border border-white/10 flex items-center gap-3">
                      <ShieldCheck className="text-emerald-500" size={24}/>
                      <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Security</p>
                          <p className="text-sm font-black text-white">AES-256 Encrypted</p>
                      </div>
                  </div>
                  <div className="glass-panel p-4 rounded-2xl border border-white/10 flex items-center gap-3">
                      <Sparkles className="text-orange-500" size={24}/>
                      <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Intelligence</p>
                          <p className="text-sm font-black text-white">Fox AI Engine v2.0</p>
                      </div>
                  </div>
              </div>
              <p className="text-[10px] text-slate-600 font-medium uppercase tracking-widest">
                  © 2025 BüroFuchs Digital • Frankfurt am Main
              </p>
          </div>
      </div>

      {/* RIGHT SIDE: AUTH FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
              <div className="text-center mb-10 lg:hidden">
                  <FoxLogo size={48} className="text-orange-500 mx-auto mb-4" />
                  <h2 className="text-3xl font-black text-white uppercase tracking-tight">BüroFuchs</h2>
              </div>

              <div className="glass-panel p-8 md:p-10 rounded-[2.5rem] border border-white/10 shadow-2xl bg-[#111827]/80 backdrop-blur-xl">
                  
                  <div className="text-center mb-8">
                      <h2 className="text-2xl font-black text-white mb-2">{isLogin ? (t('welcome_back') || 'Welcome Back') : (t('create_account') || 'Create Account')}</h2>
                      <p className="text-sm text-slate-400">
                          {isLogin ? (t('login_subtitle') || 'Enter your credentials to access your vault.') : (t('register_subtitle') || 'Start your journey with a free account.')}
                      </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                      <AnimatePresence mode="popLayout">
                          {!isLogin && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                                  <div className="space-y-2">
                                      <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{t('label_fullname') || 'Full Name'}</Label>
                                      <div className="relative">
                                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                          <Input 
                                            required={!isLogin}
                                            placeholder="John Doe" 
                                            value={formData.name}
                                            onChange={e => setFormData({...formData, name: e.target.value})}
                                            className="pl-12 h-12 bg-white/5 border-white/10 rounded-xl text-white focus:bg-white/10"
                                          />
                                      </div>
                                  </div>
                              </motion.div>
                          )}
                      </AnimatePresence>

                      <div className="space-y-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{t('label_email') || 'Email Address'}</Label>
                          <div className="relative">
                              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                              <Input 
                                type="email" required
                                placeholder="name@example.com" 
                                value={formData.email}
                                onChange={e => setFormData({...formData, email: e.target.value})}
                                className="pl-12 h-12 bg-white/5 border-white/10 rounded-xl text-white focus:bg-white/10"
                              />
                          </div>
                      </div>

                      <div className="space-y-2">
                          <div className="flex justify-between items-center ml-1">
                              <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('label_password') || 'Password'}</Label>
                              {isLogin && <a href="#" className="text-[10px] font-bold text-blue-400 hover:text-blue-300 uppercase tracking-wider">{t('forgot_password') || 'Forgot?'}</a>}
                          </div>
                          <div className="relative">
                              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                              <Input 
                                type="password" required
                                placeholder="••••••••" 
                                value={formData.password}
                                onChange={e => setFormData({...formData, password: e.target.value})}
                                className="pl-12 h-12 bg-white/5 border-white/10 rounded-xl text-white focus:bg-white/10"
                              />
                          </div>
                      </div>

                      <Button disabled={loading} className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl font-black uppercase tracking-widest text-xs shadow-lg shadow-blue-900/20 mt-4 transition-all active:scale-95">
                          {loading ? (t('btn_processing') || 'Processing...') : (isLogin ? (t('btn_signin') || 'Sign In') : (t('btn_signup') || 'Create Account'))} <ArrowRight size={16} className="ml-2"/>
                      </Button>
                  </form>

                  <div className="relative my-8">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                      <div className="relative flex justify-center text-xs uppercase"><span className="bg-[#161e2e] px-2 text-slate-500 font-bold tracking-widest">{t('or_continue') || 'Or continue with'}</span></div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                      {/* 🔥 Google Butonu Artık performGoogleLogin fonksiyonunu çağırıyor 🔥 */}
                      <Button onClick={() => performGoogleLogin()} disabled={loading} variant="outline" className="h-12 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl">
                          <Chrome size={18} className="mr-2"/> Google
                      </Button>
                      <Button variant="outline" className="h-12 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl">
                          <Github size={18} className="mr-2"/> Apple
                      </Button>
                  </div>

                  <p className="text-center mt-8 text-xs text-slate-400">
                      {isLogin ? (t('no_account') || "Don't have an account?") : (t('have_account') || "Already have an account?")}
                      <button 
                        onClick={() => setIsLogin(!isLogin)} 
                        className="ml-2 font-bold text-blue-400 hover:text-blue-300 uppercase tracking-wider transition-colors"
                      >
                          {isLogin ? (t('btn_signup') || 'Sign Up') : (t('btn_signin') || 'Log In')}
                      </button>
                  </p>

              </div>
          </motion.div>
      </div>

    </div>
  );
};

export default LoginScreen;